import { Trash2, ChevronDown } from 'lucide-react'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'

import { Button, cn, formatDay, formatPoints, pluralize } from '@/features/issue'

import { BacklogRow } from './BacklogRow'
import type { BacklogSection, Selection } from './useBacklog'

const SPRINT_STATUS_STYLES: Record<string, string> = {
  planning: 'bg-ink-100 text-ink-600 dark:bg-ink-800 dark:text-ink-300',
  active: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  completed: 'bg-ink-100 text-ink-500 dark:bg-ink-800 dark:text-ink-400',
}

/**
 * A sprint (or the backlog) with its issues.
 *
 * The list body is the drop target, so an empty sprint can still be filled —
 * which is the whole point of the planning screen.
 */
export function SprintSection({
  section,
  selection,
  canWrite,
  collapsed,
  onToggleCollapsed,
  onOpenIssue,
  onCreate,
  onStart,
  onComplete,
  onDelete,
  busy,
  hiddenCount,
}: {
  section: BacklogSection
  selection: Selection
  canWrite: boolean
  collapsed: boolean
  onToggleCollapsed: () => void
  onOpenIssue: (ref: string) => void
  onCreate: () => void
  onStart?: () => void
  onComplete?: () => void
  onDelete?: () => void
  busy?: boolean
  hiddenCount: number
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: section.key,
    data: { type: 'section', sectionKey: section.key },
  })

  const sprint = section.sprint
  const title = sprint?.name ?? 'Backlog'
  const status = sprint?.status ?? null

  const issues = section.issues
  const selectedHere = issues.filter((issue) => selection.isSelected(issue.id)).length
  const allSelected = issues.length > 0 && selectedHere === issues.length
  const someSelected = selectedHere > 0

  return (
    <section
      aria-label={title}
      className="overflow-hidden rounded-card border border-ink-200 dark:border-ink-800"
    >
      <header className="flex flex-wrap items-center gap-2 bg-ink-50 px-2 py-2 dark:bg-ink-900">
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-expanded={!collapsed}
          className="rounded p-0.5 text-ink-400 transition hover:bg-ink-200 hover:text-ink-700 dark:hover:bg-ink-800"
        >
          <ChevronDown className={cn('size-3.5 transition-transform', collapsed ? '-rotate-90' : '')} aria-hidden="true" />
        </button>

        {/* Select every issue in this section. It only ever touches this
            section, so ticking one group cannot silently clear another. The
            indeterminate state matters: without it, a partly-selected group
            looks identical to an empty one. */}
        {issues.length > 0 && (
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = someSelected && !allSelected
            }}
            onChange={() => selection.setMany(issues.map((issue) => issue.id), !allSelected)}
            aria-label={allSelected ? `Deselect all in ${title}` : `Select all in ${title}`}
            title={allSelected ? 'Deselect all in this group' : 'Select all in this group'}
            className="size-3.5 shrink-0 rounded border-ink-300 accent-brand-600"
          />
        )}

        <h2 className="text-sm font-semibold text-ink-800 dark:text-ink-100">{title}</h2>

        {status && (
          <span
            className={cn(
              'rounded px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide',
              SPRINT_STATUS_STYLES[status] ?? SPRINT_STATUS_STYLES.planning,
            )}
          >
            {status}
          </span>
        )}

        {sprint?.start_date && (
          <span className="text-xs text-ink-500">
            {formatDay(sprint.start_date)}
            {sprint.end_date ? ` – ${formatDay(sprint.end_date)}` : ''}
          </span>
        )}

        {sprint?.goal && (
          <span className="hidden max-w-xs truncate text-xs text-ink-500 md:inline">
            {sprint.goal}
          </span>
        )}

        <span className="ml-auto flex items-center gap-2 text-xs text-ink-500">
          <span>{pluralize(section.count, 'issue')}</span>
          {section.points > 0 && <span>{formatPoints(section.points)} pts</span>}
          {hiddenCount > 0 && <span className="text-ink-400">({hiddenCount} hidden)</span>}
        </span>

        {canWrite && (
          <span className="flex items-center gap-1">
            {onStart && (
              <Button size="sm" variant="primary" onClick={onStart} disabled={busy}>
                Start sprint
              </Button>
            )}
            {onComplete && (
              <Button size="sm" onClick={onComplete} disabled={busy}>
                Complete sprint
              </Button>
            )}
            {onDelete && (
              <Button size="sm" variant="ghost" onClick={onDelete} disabled={busy}
                      title="Delete this sprint and return its issues to the backlog">
                <Trash2 className="size-3.5" aria-hidden="true" />
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={onCreate}>
              + Issue
            </Button>
          </span>
        )}
      </header>

      {!collapsed && (
        <div
          ref={setNodeRef}
          className={cn('min-h-12 transition', isOver && 'bg-brand-50/60 dark:bg-brand-950/30')}
        >
          <SortableContext
            items={section.issues.map((issue) => issue.id)}
            strategy={verticalListSortingStrategy}
          >
            <ul>
              {section.issues.map((issue) => (
                <BacklogRow
                  key={issue.id}
                  issue={issue}
                  selected={selection.isSelected(issue.id)}
                  onOpen={onOpenIssue}
                  onSelect={selection.onSelect}
                  dragDisabled={!canWrite}
                />
              ))}
            </ul>
          </SortableContext>

          {section.issues.length === 0 && (
            <p className="px-3 py-4 text-center text-xs text-ink-400">
              {hiddenCount > 0
                ? `${hiddenCount} issues hidden by filters`
                : sprint
                  ? 'Drag issues here to plan this sprint.'
                  : 'Nothing in the backlog.'}
            </p>
          )}
        </div>
      )}
    </section>
  )
}
